import type { ExportColumnId } from 'components/superreports/ReportExport/types';
import {
  COLUMN_BILLABLE,
  COLUMN_BILLABLE_HOURS,
  COLUMN_BUDGET,
  COLUMN_CAPACITY,
  COLUMN_CLIENT,
  COLUMN_ESTIMATED_HOURS,
  COLUMN_ESTIMATED_INTERNAL_COST,
  COLUMN_ESTIMATED_MONEY,
  COLUMN_INTERNAL_COST,
  COLUMN_LINKED_WORK_ID,
  COLUMN_LINKED_WORK_JIRA_CUSTOM_FIELD,
  COLUMN_LINKED_WORK_STATUS,
  COLUMN_LINKED_WORK_TITLE,
  COLUMN_LOGGED_HOURS,
  COLUMN_LOGGED_MONEY,
  COLUMN_LOGGED_PERCENTAGE,
  COLUMN_NAME,
  COLUMN_NAMES,
  COLUMN_NON_BILLABLE_HOURS,
  COLUMN_OVERTIME,
  COLUMN_PROFIT,
  COLUMN_PROFITABILITY,
  COLUMN_PROJECT,
  COLUMN_PROJECT_DESCRIPTION,
  COLUMN_STATE,
  COLUMN_TAG_HEADERS,
  COLUMN_TAGS,
  COLUMN_TIMESTAMPS,
  COLUMN_UNDERTIME,
  COLUMN_USER_CUSTOM_PROPERTIES,
} from 'constants/filters';

export const COLUMN_HOUR_BILLED = 'hour_billed';
export const COLUMN_HOUR_DATE = 'hour_date';
export const COLUMN_HOUR_NOTE = 'hour_note';
export const COLUMN_HOUR_LABELS = 'hour_labels';
export const COLUMN_EMAIL = 'email';
export const COLUMN_EXTERNAL_ID = 'external_id';
export const COLUMN_USER_EXTERNAL_ID = 'user_external_id';
export const COLUMN_EXTERNAL_TICKET_ID = 'external_links';
export const COLUMN_TEAMS = 'teams';
export const COLUMN_HOUR_STATE = 'hour_state';

export const HOUR_COLUMNS = [
  COLUMN_LOGGED_HOURS,
  COLUMN_LOGGED_MONEY,
  COLUMN_LOGGED_PERCENTAGE,
  COLUMN_ESTIMATED_HOURS,
  COLUMN_ESTIMATED_MONEY,
  COLUMN_BILLABLE_HOURS,
  COLUMN_NON_BILLABLE_HOURS,
  COLUMN_HOUR_BILLED,
  COLUMN_HOUR_DATE,
  COLUMN_HOUR_NOTE,
  COLUMN_HOUR_LABELS,
  COLUMN_TIMESTAMPS,
  COLUMN_BUDGET,
  COLUMN_NAME,
  COLUMN_INTERNAL_COST,
  COLUMN_ESTIMATED_INTERNAL_COST,
  COLUMN_EMAIL,
  COLUMN_TEAMS,
  COLUMN_CAPACITY,
  COLUMN_TAG_HEADERS,
  COLUMN_STATE,
  COLUMN_USER_CUSTOM_PROPERTIES,
  COLUMN_EXTERNAL_ID,
  COLUMN_USER_EXTERNAL_ID,
  COLUMN_EXTERNAL_TICKET_ID,
  COLUMN_PROJECT_DESCRIPTION,
  COLUMN_OVERTIME,
  COLUMN_UNDERTIME,
  COLUMN_CLIENT,
  COLUMN_PROJECT,
  COLUMN_PROFIT,
  COLUMN_TAGS,
  COLUMN_BILLABLE,
  COLUMN_PROFITABILITY,
  COLUMN_HOUR_STATE,
  COLUMN_TAG_HEADERS,
  COLUMN_LINKED_WORK_ID,
  COLUMN_LINKED_WORK_TITLE,
  COLUMN_LINKED_WORK_STATUS,
  COLUMN_LINKED_WORK_JIRA_CUSTOM_FIELD,
] as const;

export const TASK_TITLE = 'task_title';
export const TASK_DESCRIPTION = 'task_description';
export const TASK_STATUS = 'status';
export const TASK_ESTIMATED_HOURS = 'task_estimated_hours';
export const TASK_LOGGED_HOURS = 'task_logged_hours';
export const TASK_PLANNED_HOURS = 'planned_hours';
export const TASK_REMAINING_HOURS = 'remaining_hours';
export const TASK_USED_PERCENT = 'used_percent';
export const TASK_START_DATE = 'from';
export const TASK_DUE_DATE = 'to';
export const TASK_ASSIGNEE = 'username';

export const TASK_COLUMNS = [
  TASK_TITLE,
  TASK_LOGGED_HOURS,
  TASK_USED_PERCENT,
  TASK_DESCRIPTION,
  TASK_STATUS,
  TASK_ASSIGNEE,
  TASK_ESTIMATED_HOURS,
  TASK_PLANNED_HOURS,
  TASK_REMAINING_HOURS,
  TASK_START_DATE,
  TASK_DUE_DATE,
] as const;

export type TaskColumn = (typeof TASK_COLUMNS)[number];

export type HourColumn = (typeof HOUR_COLUMNS)[number];

export const HOUR_COLUMN_NAMES = {
  ...COLUMN_NAMES,
  [COLUMN_HOUR_BILLED]: 'Hour billed status',
  [COLUMN_HOUR_DATE]: 'Hour date',
  [COLUMN_HOUR_NOTE]: 'Hour note',
  [COLUMN_HOUR_LABELS]: 'Hour tags',
  [COLUMN_TAGS]: 'Tags',
  [COLUMN_EMAIL]: 'Email',
  [COLUMN_EXTERNAL_ID]: 'Project External ID',
  [COLUMN_USER_EXTERNAL_ID]: 'User External ID',
  [COLUMN_EXTERNAL_TICKET_ID]: 'External Ticket ID',
  [COLUMN_TEAMS]: 'Teams',
  [COLUMN_TAG_HEADERS]: 'Tag headers',
  [COLUMN_USER_CUSTOM_PROPERTIES]: 'Custom user fields',
  [COLUMN_CLIENT]: 'Client',
  [COLUMN_HOUR_STATE]: 'State',
  [COLUMN_LINKED_WORK_ID]: 'Linked Work Item ID',
  [COLUMN_LINKED_WORK_TITLE]: 'Linked Work Item Title',
  [COLUMN_LINKED_WORK_STATUS]: 'Linked Work Item Sync Status',
  [COLUMN_LINKED_WORK_JIRA_CUSTOM_FIELD]: 'Jira Custom Field',
};

export const TASK_COLUMN_NAMES = {
  [TASK_TITLE]: 'Task title',
  [TASK_DESCRIPTION]: 'Task description',
  [TASK_STATUS]: 'Status',
  [TASK_ASSIGNEE]: 'Assignee',
  [TASK_ESTIMATED_HOURS]: 'Task estimated hours',
  [TASK_LOGGED_HOURS]: 'Task logged hours',
  [TASK_USED_PERCENT]: 'Used (%)',
  [TASK_PLANNED_HOURS]: 'Planned hours',
  [TASK_REMAINING_HOURS]: 'Remaining hours',
  [TASK_START_DATE]: 'Start date',
  [TASK_DUE_DATE]: 'Due date',
};

export const EXPORT_COLUMN_LABELS: Record<ExportColumnId, string> = {
  ...HOUR_COLUMN_NAMES,
  ...TASK_COLUMN_NAMES,
};

export const exportFileExtensions = ['pdf', 'xlsx', 'xls', 'csv'] as const;

export type SupportedExtensions = (typeof exportFileExtensions)[number];

export interface ExportOption {
  name: string;
  extension: SupportedExtensions;
  icon: string;
}

export const downloadOptions: Array<ExportOption> = [
  { name: 'Excel', extension: 'xlsx', icon: 'file-excel-o' },
  { name: 'PDF', extension: 'pdf', icon: 'file-pdf-o' },
  { name: 'CSV', extension: 'csv', icon: 'file-o' },
];

export const taskDownloadOptions: Array<ExportOption> = [
  { name: 'Excel', extension: 'xlsx', icon: 'file-excel-o' },
  { name: 'CSV', extension: 'csv', icon: 'file-o' },
];
